// frontend/src/pages/ResultsPage.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/outline';
import { getReport } from '../services/api';
import { getTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext';

const ResultsPage = () => {
    const { accessToken } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportGenerated, setReportGenerated] = useState(false);
    const [candidateName, setCandidateName] = useState('');
    const { language } = useLanguage();

    const t = (key, replacements) => getTranslation(language, key, replacements);

    useEffect(() => {
        // Get language from session
        const fetchLang = async () => {
            try {
                const res = await api.get(`/sessions/${accessToken}/status`);
                const lang = res.data.session?.language || 'en';
                setLanguage(lang);
            } catch (err) {
                console.error(err);
            }
        };
        fetchLang();
    }, [accessToken]);

    useEffect(() => {
        if (accessToken) {
            //generateReportInBackground();
        }
    }, [accessToken]);

    // const generateReportInBackground = async () => {
    //     try {
    //         setLoading(true);

    //         const response = await getReport(accessToken);

    //         if (response.data.success) {
    //             setCandidateName(response.data.report?.candidate?.name || 'Candidate');
    //             setReportGenerated(true);

    //             try {
    //                 await fetch(`/api/reports/${accessToken}/generate-pdf`, {
    //                     method: 'POST'
    //                 });
    //             } catch (pdfError) {
    //                 console.log('PDF generation will be handled later');
    //             }
    //         } else {
    //             setError('Unable to generate report. Please contact support.');
    //         }
    //     } catch (err) {
    //         console.error('Error generating report:', err);
    //         setError('Unable to generate report. Please contact support.');
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleCloseBrowser = () => {
        window.close();

        // Fallback if window.close() doesn't work
        setTimeout(() => {
            window.location.href = 'about:blank';
        }, 500);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff9f2f] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Submitting your assessment...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border border-gray-200">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Something Went Wrong</h2>
                    <p className="text-gray-600">{error}</p>
                    <button
                        onClick={handleCloseBrowser}
                        className="mt-4 px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a]"
                    >
                        Close Browser
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f4f6fb] flex items-center justify-center py-8">
            <div className="max-w-lg w-full mx-auto px-4">
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircleIcon className="h-12 w-12 text-green-600" />
                    </div>

                    <h1 className="text-3xl font-bold text-[#063b56] mb-2">
                        {t('result.title')}
                    </h1>
                    <p className="text-gray-600 mb-6">
                        {t('result.subtitle')}
                    </p>

                    <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
                        <p className="text-sm text-blue-800 mb-3">
                            {t('result.message')}
                        </p>
                    </div>

                    <div className="text-left bg-gray-50 rounded-lg p-4">
                        <p className="font-semibold text-gray-700 mb-2">
                            {t('result.what_next')}
                        </p>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                {t('result.next_step_1')}
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                {t('result.next_step_2')}
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-500 mr-2">✓</span>
                                {t('result.next_step_3')}
                            </li>
                        </ul>
                    </div>

                    <p className="text-xs text-gray-400 mt-6">
                        {t('result.reference', { id: accessToken?.substring(0, 12) + '...' })}
                    </p>

                    <p className="text-xs text-gray-400 mt-2">
                        {t('result.contact')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ResultsPage;